import React from 'react';
import { AlertTriangle, X, User } from 'lucide-react';
import { useAuth } from '../AuthProvider';

interface SessionExpiredBannerProps {
  className?: string;
  /** If provided, shows "Continue as {email}" button */
  onContinueAs?: () => void;
}

/**
 * Session Expired Banner
 * Shows a friendly message when the user's session has expired
 */
export const SessionExpiredBanner: React.FC<SessionExpiredBannerProps> = ({ 
  className = '',
  onContinueAs,
}) => {
  const { sessionExpiredMessage, clearSessionExpired } = useAuth();

  if (!sessionExpiredMessage) return null;

  // Try to get last known email from localStorage
  let lastEmail: string | null = null;
  try {
    const storedEmail = localStorage.getItem('bright_last_user_email');
    if (storedEmail) {
      lastEmail = storedEmail;
    }
  } catch (e) {
    // localStorage may not be available
  }

  return (
    <div
      className={`bg-warning/10 border border-warning/30 rounded-lg p-4 mb-4 ${className}`}
      role="alert"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">Session Expired</p>
          <p className="text-sm text-muted-foreground mt-1">
            {sessionExpiredMessage}
          </p>
          
          {lastEmail && onContinueAs && (
            <button
              onClick={onContinueAs}
              className="mt-3 flex items-center gap-2 text-sm text-primary hover:underline"
            >
              <User className="w-4 h-4" />
              Continue as {lastEmail}
            </button>
          )}
        </div>
        <button
          onClick={clearSessionExpired}
          className="p-1 hover:bg-muted rounded transition-colors shrink-0"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>
    </div>
  );
};

export default SessionExpiredBanner;
